import { Component, OnInit, PLATFORM_ID, inject, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Company } from '@core/models/company';
import { User } from '@core/models/user';
import { AuthFlowService } from '@core/services/auth-flow.service';
import { AuthService } from '@core/services/auth.service';
import { CompanyService } from '@core/services/company.service';
import { LoggingService } from '@core/services/logging.service';
import { UserService } from '@core/services/user.service';

/** Where a company with no plan is sent: there is nothing else it can use yet. */
export const PLAN_SELECTION_ROUTE = '/dashboard/company/subscription-plans';

interface ValidationMessage {
  id: string;
  name: string;
  status: boolean | 'error';
}

interface ValidationStep {
  id: string;
  name: string;
  run: () => Promise<boolean>;
}

/**
 * Runs the post-login validation sequence before routing the user onward.
 */
@Component({
  selector: 'app-wait-page',
  templateUrl: './wait-page.component.html',
  styleUrls: ['./wait-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WaitPageComponent implements OnInit {
  private authService = inject(AuthService);
  private authFlowService = inject(AuthFlowService);
  private router = inject(Router);
  private companyService = inject(CompanyService);
  private loggingService = inject(LoggingService);
  private userService = inject(UserService);
  private platformId = inject(PLATFORM_ID);

  private company: Company | null = null;

  user: User | null = null;
  hasError = false;
  messages: ValidationMessage[] = [];

  ngOnInit(): void {
    void this.bootstrap();
  }

  /**
   * Resolves the next route and performs any required paid-account validation.
   */
  private async bootstrap(): Promise<void> {
    try {
      this.user = await this.authService.waitForHydratedUser(10000);
      if (!this.user) {
        await this.navigateToLogin();
        return;
      }

      void this.userService.recordAcquisitionAttribution(this.user).catch(() => undefined);

      const nextRoute = await this.authFlowService.getPostLoginRoute(this.user);
      if (nextRoute.startsWith('/register/')) {
        await this.router.navigateByUrl(nextRoute, { replaceUrl: true });
        return;
      }

      const validationSteps = this.buildValidationSteps(this.user);
      this.messages = validationSteps.map(({ id, name }) => ({
        id,
        name,
        status: false,
      }));

      for (const step of validationSteps) {
        const succeeded = await step.run();
        this.updateStepStatus(step.id, succeeded ? true : 'error');
        if (!succeeded) {
          this.hasError = true;
          return;
        }
      }

      if (
        isPlatformBrowser(this.platformId) &&
        typeof sessionStorage !== 'undefined'
      ) {
        sessionStorage.removeItem('redirectUrl');
      }

      await this.router.navigateByUrl(this.resolveDestination(nextRoute), { replaceUrl: true });
    } catch (error) {
      this.hasError = true;
      this.loggingService.logError('Post-login validation failed', { error });
    }
  }

  /**
   * Sends a company with no plan to plan selection. A new signup has no
   * scanning and no AI until checkout (#847), so an empty dashboard is a dead
   * end; the plans page is the next step (#870).
   *
   * @param nextRoute The route the post-login flow resolved.
   * @returns The route to navigate to.
   */
  private resolveDestination(nextRoute: string): string {
    const tier = this.company?.tier;
    return !tier || tier === 'free' ? PLAN_SELECTION_ROUTE : nextRoute;
  }

  /**
   * Builds the visible validation steps for the current user tier.
   */
  private buildValidationSteps(user: User): ValidationStep[] {
    const steps: ValidationStep[] = [
      {
        id: 'company',
        name: 'Verifying company',
        run: () => this.validateCompany(user),
      },
      {
        id: 'subscription',
        name: 'Verifying subscription',
        run: () => this.validateSubscription(),
      },
    ];

    const userTier = user.intendedTier || 'free';
    if (userTier === 'trial' || userTier === 'premium') {
      steps.push(
        {
          id: 'account',
          name: 'Verifying account',
          run: () => this.validateAccount(user),
        },
        {
          id: 'credentials',
          name: 'Validating user credentials',
          run: () => this.validateCredentials(user),
        },
      );
    }

    steps.push({
      id: 'dashboard',
      name: 'Setting up dashboard',
      run: async () => true,
    });

    return steps;
  }

  /**
   * Loads the active company record linked to the user.
   */
  private async validateCompany(user: User): Promise<boolean> {
    const companyIds = [
      user.currentCompanyId,
      ...(Array.isArray(user.companies) ? user.companies : []),
    ].filter((companyId): companyId is string => !!companyId);

    for (const companyId of Array.from(new Set(companyIds))) {
      try {
        const companyData = await this.companyService.getDocument(companyId);
        if (companyData?.uid) {
          this.company = new Company(companyData);
          return true;
        }
      } catch {
        // Ignore broken company references and keep trying linked companies.
      }
    }

    return false;
  }

  /**
   * Confirms the active company has a tier assigned.
   */
  private async validateSubscription(): Promise<boolean> {
    return !!this.company?.tier;
  }

  /**
   * Verifies that the user's account is still active.
   */
  private async validateAccount(user: User): Promise<boolean> {
    return !(await this.authService.isAccountExpired(user));
  }

  /**
   * Ensures the hydrated user contains the required API identity fields.
   */
  private async validateCredentials(user: User): Promise<boolean> {
    return !!user.uid && !!user.email && !!user.currentCompanyId;
  }

  /**
   * Marks a validation step as complete or failed.
   */
  private updateStepStatus(
    stepId: string,
    status: boolean | 'error',
  ): void {
    this.messages = this.messages.map((message) =>
      message.id === stepId ? { ...message, status } : message,
    );
  }

  /**
   * Signs the user out from the error state and returns to login.
   */
  async logout(): Promise<void> {
    await this.authService.signOutUser();
    await this.navigateToLogin();
  }

  /**
   * Navigates back to the login page.
   */
  private async navigateToLogin(): Promise<void> {
    await this.router.navigate(['/login'], { replaceUrl: true });
  }
}
